import type { Session } from '@supabase/supabase-js';
import type { InventorySnapshot } from './data';
import { supabasePublishableKey, supabaseUrl } from './runtime';

export type Person = { id: string; name: string; role: 'admin' | 'member'; color: string; email?: string; avatar_url?: string; allowed_agents?: string[] | null };
export type HistoryRow = { agent_id: string; messages: CloudMessage[] };
export type CloudMessage = { role: 'user' | 'assistant'; content: string; created_at?: string };
export type HealthLane = {
  agent_id: string;
  created_at?: string;
  source_at?: string;
  source_note?: string;
  last_success_at?: string;
  next_run_at?: string;
  last_error?: string;
};
export type NotificationPreferences = { enabled: boolean; last_seen_at?: string | null };
export type BusinessChange = {
  id: string;
  report_id: string;
  agent_id: string;
  created_at: string;
  source_at?: string | null;
  kind: 'risk' | 'improvement';
  label: 'NEEDS ATTENTION' | 'CHANGED' | 'IMPROVED';
  severity: string;
  headline: string;
  summary: string;
  action: string;
};

export type OperationalActionStatus = 'new' | 'in_progress' | 'waiting' | 'done' | 'ignored';
export type OperationalAction = {
  id: string;
  laneId: 'shopify-retail' | 'shopify-wholesale';
  type: 'payment' | 'inventory';
  title: string;
  detail: string;
  amount?: number | null;
  recommendedQuantity?: number | null;
  dueAt?: string | null;
  urgency: 'urgent' | 'watch';
  recommendedAction: string;
  status: OperationalActionStatus;
  assignedTo?: string | null;
  note?: string | null;
  sourceAt?: string | null;
};
export type OperationsSummary = {
  open: number;
  urgent: number;
  paymentValue: number;
  assignedToMe: number;
};
export type SocialPerformanceSnapshot = {
  sourceAt: string;
  periodLabel: string;
  source: string;
  metrics: Record<string, number>;
  topContent: { title: string; format?: string; reach?: number; shares?: number; saves?: number; clicks?: number }[];
  recommendations: string[];
};

async function request<T>(route: string, token: string, body: unknown): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 35_000);
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/blonde/${route}`, {
      method: 'POST',
      headers: { apikey: supabasePublishableKey, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
      signal: controller.signal,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Blonde could not complete this request.');
    return result as T;
  } catch (error) {
    if (controller.signal.aborted) throw new Error('Blonde’s data service is taking too long. Please try again.');
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

export async function signInWithPin(pin: string) {
  const result = await request<{ access_token: string; refresh_token: string }>('pin-login', supabasePublishableKey, { pin });
  return result;
}

export function manageMember(session: Session, body: unknown) {
  return request<any>('admin-members', session.access_token, body);
}

export function listQna(session: Session) {
  return request<{ history: HistoryRow[] }>('qna-history', session.access_token, { action: 'list' });
}

export function syncQna(session: Session, agentId: string, messages: CloudMessage[]) {
  return request('qna-history', session.access_token, { action: 'sync', agent_id: agentId, messages });
}

export function deleteQna(session: Session, agentId: string, question: string, createdAt?: string) {
  return request('qna-history', session.access_token, { action: 'delete-entry', agent_id: agentId, question, created_at: createdAt || '' });
}

export function updateProfile(session: Session, name: string, avatarUrl: string) {
  return request('profile-update', session.access_token, { name, avatar_url: avatarUrl });
}

export function loadNotificationPreferences(session: Session) {
  return request<{ prefs?: { notifications?: NotificationPreferences } }>('preferences', session.access_token, { action: 'get' });
}

export function saveNotificationPreferences(session: Session, notifications: NotificationPreferences) {
  return request('preferences', session.access_token, { notifications });
}

export function loadBusinessChanges(session: Session) {
  return request<{ changes: BusinessChange[]; unread_count: number; prefs?: { notifications?: NotificationPreferences } }>(
    'business-changes', session.access_token, { action: 'list' },
  );
}

export function markBusinessChangesSeen(session: Session) {
  return request<{ ok: boolean; last_seen_at: string }>('business-changes', session.access_token, { action: 'mark-seen' });
}

export function loadSavedReports(session: Session) {
  return request<{ reports: any[] }>('saved-reports', session.access_token, {});
}

export function loadInventorySnapshots(session: Session) {
  return request<{ snapshots: InventorySnapshot[] }>('inventory-snapshots', session.access_token, {});
}

export function loadOperations(session: Session) {
  return request<{ actions: OperationalAction[]; summary: OperationsSummary }>('operations', session.access_token, { action: 'list' });
}

export function updateOperationalAction(
  session: Session,
  action: Pick<OperationalAction, 'id' | 'laneId' | 'type'> & {
    status: OperationalActionStatus;
    assignedTo?: string | null;
    note?: string | null;
    dueAt?: string | null;
  },
) {
  return request<{ ok: boolean; action: OperationalAction }>('operations', session.access_token, {
    action: 'update',
    action_key: action.id,
    lane_id: action.laneId,
    action_type: action.type,
    status: action.status,
    assigned_to: action.assignedTo ?? null,
    note: action.note ?? null,
    due_at: action.dueAt ?? null,
  });
}

export function loadSocialPerformance(session: Session) {
  return request<{ snapshot: SocialPerformanceSnapshot | null }>('social-performance', session.access_token, {});
}

export function toggleSavedReport(session: Session, reportId: string) {
  return request<{ saved: boolean }>('saved-reports', session.access_token, { report_id: reportId });
}

export function updateReportAction(
  session: Session,
  reportId: string,
  action: 'acknowledge' | 'resolve' | 'assign',
  value: boolean | string | null,
) {
  return request<{ ok: boolean }>('report-action', session.access_token, {
    report_id: reportId,
    action,
    value,
  });
}

export function loadSystemHealth(session: Session) {
  return request<{ lanes: HealthLane[] }>('system-health', session.access_token, {});
}

export function recordUsage(session: Session, event: string, metadata: Record<string, unknown> = {}) {
  return request('usage-event', session.access_token, { event, metadata });
}

const VAPID_PUBLIC_KEY='BFR9atM4yT16BhWfozllRPzjulpsktg2F06AIFFlotQqcBuJZGptljq75iuDfszes1DinP45ncMXqkwNpHKdvoM';
function pushKey(value:string){const padding='='.repeat((4-value.length%4)%4);const raw=window.atob((value+padding).replace(/-/g,'+').replace(/_/g,'/'));return Uint8Array.from(raw,(character)=>character.charCodeAt(0));}

export async function enablePushNotifications(session:Session){
  if(typeof window==='undefined'||!('Notification'in window)||!('serviceWorker'in navigator)||!('PushManager'in window))return'unsupported';
  const permission=await window.Notification.requestPermission();
  if(permission!=='granted')return permission;
  const registration=await navigator.serviceWorker.ready;
  const subscription=await registration.pushManager.getSubscription()||await registration.pushManager.subscribe({userVisibleOnly:true,applicationServerKey:pushKey(VAPID_PUBLIC_KEY)});
  await request('push-subscribe',session.access_token,{subscription:subscription.toJSON()});
  await registration.showNotification('Meaningful alerts are on',{body:'Blonde will stay quiet unless verified business data changes enough to matter.',tag:'blonde-notifications-enabled',icon:'/icons/icon-192.png',badge:'/icons/icon-192.png',data:{url:'/'}});
  return'granted';
}
